import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { formatCurrency, formatDisplayAmount, toBaseCurrency, formatDate, getCurrencyConfig, API_URL } from "../lib/utils";
import { printQuote as printQuoteDoc } from "../lib/quotePrint";
import { Textarea } from "../components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import { FileSignature, Plus, Trash2, ArrowRightCircle, Printer, CheckCircle2, XCircle, Pencil } from "lucide-react";

const STATUS = {
  pending: { label: "Pendente", cls: "bg-amber-500/20 text-amber-600" },
  accepted: { label: "Aceite", cls: "bg-emerald-500/20 text-emerald-600" },
  rejected: { label: "Rejeitado", cls: "bg-red-500/20 text-red-600" },
  converted: { label: "Convertido", cls: "bg-blue-500/20 text-blue-600" },
};

export default function QuotesPage() {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerNif, setCustomerNif] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const openEdit = (q) => {
    const rate = getCurrencyConfig().rate || 1;
    setEditing(q);
    setCustomerName(q.customer_name || "");
    setCustomerNif(q.customer_nif || "");
    setValidUntil(q.valid_until || "");
    setNotes(q.notes || "");
    setItems((q.items || []).map((it) => ({
      product_id: it.product_id || "",
      description: it.description,
      quantity: it.quantity,
      unit_price: (it.unit_price / rate).toString(),
      tax_rate: it.tax_rate ?? 14,
    })));
    setModal(true);
  };

  const resetForm = () => {
    setEditing(null); setItems([]); setCustomerName(""); setCustomerNif(""); setValidUntil(""); setNotes("");
  };

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [qRes, pRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/quotes`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/settings`, { headers, withCredentials: true }),
      ]);
      setQuotes(qRes.data);
      setProducts(pRes.data);
      setSettings(sRes.data || {});
    } catch { toast.error("Erro ao carregar orçamentos"); }
    finally { setLoading(false); }
  };

  const addItem = () => setItems([...items, { product_id: "", description: "", quantity: 1, unit_price: "", tax_rate: 14 }]);
  const updateItem = (i, field, value) => {
    const next = [...items];
    next[i][field] = value;
    if (field === "product_id") {
      const p = products.find((x) => x.product_id === value);
      if (p) {
        next[i].description = p.name;
        next[i].unit_price = (p.price / (getCurrencyConfig().rate || 1)).toString();
        next[i].tax_rate = p.tax_rate ?? 14;
      }
    }
    setItems(next);
  };
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totalDisplay = items.reduce((s, it) => {
    const line = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
    return s + line + line * ((parseFloat(it.tax_rate) || 0) / 100);
  }, 0);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Adicione pelo menos um item");
    const payload = {
      customer_name: customerName,
      customer_nif: customerNif || null,
      valid_until: validUntil || null,
      notes: notes || null,
      items: items.map((it) => ({
        product_id: it.product_id || null,
        description: it.description,
        quantity: parseFloat(it.quantity) || 0,
        unit_price: toBaseCurrency(it.unit_price),
        tax_rate: parseFloat(it.tax_rate) || 0,
      })),
    };
    try {
      if (editing) {
        await axios.put(`${API_URL}/quotes/${editing.quote_id}`, payload, { headers, withCredentials: true });
        toast.success(`Orçamento ${editing.number} atualizado`);
      } else {
        await axios.post(`${API_URL}/quotes`, payload, { headers, withCredentials: true });
        toast.success("Orçamento criado");
      }
      setModal(false); resetForm();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || (editing ? "Erro ao atualizar orçamento" : "Erro ao criar orçamento")); }
  };

  const convert = async (q) => {
    if (!window.confirm(`Converter o orçamento ${q.number} num pedido?`)) return;
    try {
      await axios.post(`${API_URL}/quotes/${q.quote_id}/convert`, {}, { headers, withCredentials: true });
      toast.success("Orçamento convertido em pedido — veja na página Pedidos");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao converter"); }
  };

  const setStatus = async (q, status) => {
    try {
      await axios.put(`${API_URL}/quotes/${q.quote_id}/status?status=${status}`, {}, { headers, withCredentials: true });
      toast.success("Estado atualizado");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Remover orçamento ${q.number}?`)) return;
    try {
      await axios.delete(`${API_URL}/quotes/${q.quote_id}`, { headers, withCredentials: true });
      toast.success("Removido");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const printQuote = (q) => printQuoteDoc(q, settings);

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Orçamentos</h1>
          <p className="text-muted-foreground">Orçamentos/proformas — converta em pedido quando o cliente aceitar</p>
        </div>
        <Button onClick={() => { resetForm(); setItems([{ product_id: "", description: "", quantity: 1, unit_price: "", tax_rate: 14 }]); setModal(true); }} data-testid="add-quote-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Orçamento
        </Button>
      </div>

      {loading ? <p className="text-muted-foreground">A carregar...</p> :
        quotes.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <FileSignature className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nenhum orçamento criado
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {quotes.map((q) => (
              <Card key={q.quote_id} data-testid={`quote-card-${q.quote_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-mono font-bold">{q.number}</p>
                        <Badge className={STATUS[q.status]?.cls}>{STATUS[q.status]?.label}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{q.customer_name} · {formatDate(q.created_at)} · {q.items.length} item(s)</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-mono font-bold text-lg">{formatCurrency(q.total)}</p>
                      <Button size="sm" variant="outline" onClick={() => printQuote(q)} title="Imprimir / PDF" data-testid={`print-quote-${q.quote_id}`}>
                        <Printer className="w-4 h-4 mr-1" /> Imprimir
                      </Button>
                      {q.status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openEdit(q)} data-testid={`edit-quote-${q.quote_id}`}><Pencil className="w-4 h-4 mr-1" /> Editar</Button>
                          <Button size="sm" variant="outline" onClick={() => setStatus(q, "accepted")} data-testid={`accept-quote-${q.quote_id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Aceite</Button>
                          <Button size="sm" variant="outline" className="text-destructive" onClick={() => setStatus(q, "rejected")}><XCircle className="w-4 h-4" /></Button>
                        </>
                      )}
                      {["pending", "accepted"].includes(q.status) && (
                        <Button size="sm" onClick={() => convert(q)} data-testid={`convert-quote-${q.quote_id}`}>
                          <ArrowRightCircle className="w-4 h-4 mr-1" /> Converter em Pedido
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(q)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={modal} onOpenChange={(o) => { setModal(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle data-testid="quote-modal-title">{editing ? `Editar Orçamento ${editing.number}` : "Novo Orçamento"}</DialogTitle>
            <DialogDescription>{editing ? "Só é possível editar orçamentos pendentes. O número mantém-se." : "Orçamento/proforma para enviar ao cliente."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1 col-span-2">
                <Label>Cliente *</Label>
                <Input required value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="quote-customer-input" />
              </div>
              <div className="space-y-1">
                <Label>NIF</Label>
                <Input value={customerNif} onChange={(e) => setCustomerNif(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Válido até</Label>
                <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="quote-valid-until-input" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Notas / Condições (aparecem no documento)</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: Entrega em 5 dias úteis após confirmação. Sinal de 50%." data-testid="quote-notes-input" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Itens</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Item</Button>
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4">
                    <Select value={it.product_id} onValueChange={(v) => updateItem(i, "product_id", v)}>
                      <SelectTrigger className="text-xs"><SelectValue placeholder="Produto (opcional)" /></SelectTrigger>
                      <SelectContent>{products.map((pr) => <SelectItem key={pr.product_id} value={pr.product_id}>{pr.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3"><Input placeholder="Descrição" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} required /></div>
                  <div className="col-span-1"><Input type="number" step="1" min="1" placeholder="Qtd" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} required /></div>
                  <div className="col-span-2"><Input type="number" step="0.01" min="0" placeholder="Preço" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} required /></div>
                  <div className="col-span-1"><Input type="number" step="1" min="0" placeholder="IVA%" value={it.tax_rate} onChange={(e) => updateItem(i, "tax_rate", e.target.value)} /></div>
                  <div className="col-span-1"><Button type="button" size="icon" variant="ghost" className="text-destructive" onClick={() => removeItem(i)}><Trash2 className="w-4 h-4" /></Button></div>
                </div>
              ))}
              <p className="text-right font-bold">Total c/ IVA: {formatDisplayAmount(totalDisplay)}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setModal(false); resetForm(); }}>Cancelar</Button>
              <Button type="submit" data-testid="save-quote-btn">{editing ? "Guardar Alterações" : "Criar Orçamento"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
